import { Dispatch, SetStateAction } from 'react';

import { TRequiredPick } from '@interfaces/common/genericWithModifying';
import { IFlatMoTreeNode, ITreeCheckboxOption } from '@interfaces/components/tree';
import { IFlatTreeNodeInterface } from '@components/common/FlatTree/interface';

import { ETreeTypeNode } from '@enums/tree';

import {
	FLAT_NODE_PATH_SEPARATOR,
	MO_IDS_SEPARATOR,
	SELECT_TREE_ALL_CHECKED,
} from '@components/common/FlatTree/constants';

const HEIGHT_WITH_TAGS = 43;
const HEIGHT_WITHOUT_TAGS = 30;

export const getItemsSize = (node: IFlatMoTreeNode) => {
	return node?.tags?.length ? HEIGHT_WITH_TAGS : HEIGHT_WITHOUT_TAGS;
};

export const prepareTreeSelectValues = (data: string[]): ITreeCheckboxOption[] => {
	return data.map((item) => {
		return {
			value: item,
			isChecked: true,
			isSelected: true,
			nodeId: item,
		};
	});
};

type TFlatTreeNodeWithCheckboxListType = TRequiredPick<IFlatMoTreeNode, 'checkboxList'>;

export const isMavenirNodeObject = (node: IFlatTreeNodeInterface): node is TFlatTreeNodeWithCheckboxListType => {
	return node.nodeType === ETreeTypeNode.object
		|| node.nodeType === ETreeTypeNode.array
		|| node.nodeType === ETreeTypeNode.arrayObjects;
};

export const isMavenirNodeObjectsArray = (node: IFlatTreeNodeInterface): node is TFlatTreeNodeWithCheckboxListType => {
	return isMavenirNodeObject(node) && !!node.checkboxList?.length;
};

export const isChildNode = (node: IFlatTreeNodeInterface, parent: IFlatTreeNodeInterface): boolean => {
	return node.path.startsWith(parent.path + FLAT_NODE_PATH_SEPARATOR);
};

export const isSamePath = (node: IFlatTreeNodeInterface, parent: IFlatTreeNodeInterface): boolean => {
	return node.path === parent.path;
};

export const isCommonRootPath = (node: IFlatTreeNodeInterface, parent: IFlatTreeNodeInterface): boolean => {
	return isChildNode(node, parent) || isSamePath(node, parent);
};

export const isObjectNodeFullyChecked = (node: IFlatMoTreeNode): boolean => {
	return node.countOfChecked === node.countOfChildren;
};

export const getMoIdsPath = (checkboxList: ITreeCheckboxOption[]): string => {
	const path = checkboxList
		.filter(item => item.isChecked)
		.map((item) => item.nodeId)
		.join(MO_IDS_SEPARATOR);

	return path.length ? `/{${path}}` : '';
};

export const getChangedMavenirSelect = ({
	isAllChecked,
	parentCheckboxList,
	checkedValue,
	isSingleSelectMode,
}: {
	isAllChecked: boolean;
	parentCheckboxList: ITreeCheckboxOption[];
	checkedValue: string;
	isSingleSelectMode?: boolean;
}): ITreeCheckboxOption[] => {
	const checkboxList: ITreeCheckboxOption[] = [];

	for (const checkbox of parentCheckboxList) {
		if (checkbox.value === checkedValue) {
			checkboxList.push({
				...checkbox,
				isChecked: isSingleSelectMode || !checkbox.isChecked,
			});
		} else if (checkedValue === SELECT_TREE_ALL_CHECKED) {
			checkboxList.push({
				...checkbox,
				isChecked: !isAllChecked,
			});
		} else {
			checkboxList.push(isSingleSelectMode ? { ...checkbox, isChecked: false } : checkbox);
		}
	}

	return checkboxList;
};

export const toggleSelectedNodes = ({
	nodes,
	parentIndex,
}: {
	nodes: IFlatMoTreeNode[];
	parentIndex: number;
}): void => {
	const parentElement = nodes[parentIndex];
	const isChecked = !nodes[parentIndex].isChecked;
	const parentNode = nodes[parentIndex];

	if (isMavenirNodeObject(parentNode)) {
		for (let index = parentIndex; index < nodes.length; index++) {
			const node = nodes[index];
			const isCommonRoot = isCommonRootPath(node, parentElement);

			if (!isCommonRoot) {
				break;
			}

			node.countOfChecked = node.countOfChildren;

			if (isChecked) {
				node.isChecked = true;
			} else {
				node.isChecked = false;
				node.countOfChecked = 0;
			}
		}
	}

	parentNode.isChecked = isChecked;
};

export const getAncestorsObjectNodes = ({
	parentPath,
	parentIndex,
	nodesIndexes,
	nodes,
}: {
	parentPath: string;
	parentIndex: number;
	nodesIndexes: number[];
	nodes: IFlatMoTreeNode[];
}): IFlatMoTreeNode[] => {
	const ancestors = parentPath
		.split(FLAT_NODE_PATH_SEPARATOR)
		.map((_, pathIndex) => {
			return parentPath
				.split(FLAT_NODE_PATH_SEPARATOR)
				.slice(0, pathIndex)
				.join(FLAT_NODE_PATH_SEPARATOR);
		});

	return nodesIndexes
		.filter(item => item <= parentIndex && ancestors.includes(nodes[item].path))
		.map(item => nodes[item])
		.reverse();
};

export const recalculateCheckedNodes = ({
	initNode,
	ancestorNodes,
	initCheckedCount,
	setIsTreeChecked,
}: {
	initNode: IFlatMoTreeNode;
	ancestorNodes: IFlatMoTreeNode[];
	initCheckedCount: number;
	setIsTreeChecked?: Dispatch<SetStateAction<boolean>>;
}): void => {
	const {
		countOfChildren: initChildrenCount,
		nodeType: isInitNodeObject,
		isChecked: isInitNodeChecked,
	} = initNode;

	const counterSign = isInitNodeChecked ? 1 : -1;

	let counter = counterSign;
	let isTreeChecked = counterSign > 0;

	for (const node of ancestorNodes) {
		const { countOfChecked } = node;

		if (!isMavenirNodeObject(node)) continue;

		const isObjectNodeFullyCheckedStatusLost = !isInitNodeChecked && isObjectNodeFullyChecked(node);
		const isSubtractExtraCheckedCount = isInitNodeChecked && initCheckedCount;

		if (isInitNodeObject) {
			node.countOfChecked = countOfChecked + counter + initChildrenCount * counterSign;

			if (isSubtractExtraCheckedCount) node.countOfChecked -= initCheckedCount;
		} else node.countOfChecked = countOfChecked + counter;

		const isCurrentNodeFullyChecked = isObjectNodeFullyChecked(node);

		const isParameterSubtraction = isObjectNodeFullyCheckedStatusLost && !isCurrentNodeFullyChecked;

		if (isCurrentNodeFullyChecked || isParameterSubtraction) {
			node.isChecked = isInitNodeChecked;
			counter = counter + counterSign;
		}

		isTreeChecked = node.countOfChecked > 0;
	}

	if (setIsTreeChecked) {
		setIsTreeChecked(isTreeChecked);
	}
};

export const getNodePathWithMoIds = (
	data: IFlatMoTreeNode[],
	node: IFlatMoTreeNode,
	currentCheckboxList: ITreeCheckboxOption[],
): string => {
	const pathWithNodes = [];

	for (let index = 0; index < data.length; index++) {
		const item = data[index];

		if (isMavenirNodeObjectsArray(item) && isCommonRootPath(node, item)) {
			const isCurrentNode = item.path === node.path;
			const selectedNodes = getMoIdsPath(isCurrentNode ? currentCheckboxList : item.checkboxList);

			pathWithNodes.push(item.key + selectedNodes);

			if (isCurrentNode) {
				break;
			}
		} else if (isCommonRootPath(node, item)) {
			pathWithNodes.push(item.key);
		}
	}

	return pathWithNodes.join(FLAT_NODE_PATH_SEPARATOR);
};
